import { Toggle } from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React, { useEffect, useRef } from 'react';

import css from './ToggleFocusRing.module.css';

type TSearchToggleProps = React.ComponentProps<typeof Toggle>;

/**
 * Bonfire's Toggle with the two keyboard affordances it is missing.
 *
 * The focus ring: Toggle calls `useFocusRing()` and then discards `isFocusVisible`,
 * leaving the switch with no focus indicator at all. See ToggleFocusRing.module.css
 * for why the class goes on the root rather than on the focused element.
 *
 * Enter: Toggle renders a checkbox, and a checkbox natively answers to Space alone.
 * Inside the search form that made Enter worse than inert — it submitted the search,
 * which with no destination filled in threw focus into the location field and closed
 * the panel out from under the user.
 */
export const SearchToggle: React.FC<TSearchToggleProps> = ({
  className,
  isSelected,
  onChange,
  ...toggleProps
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Toggle forwards no DOM props to its input, but it does forward a ref to it, so
  // the key is handled on the focused element itself rather than through a wrapper.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return;

      // Both halves matter: flip the switch, and keep the form from submitting.
      event.preventDefault();
      onChange?.(!isSelected);
    };

    input.addEventListener('keydown', handleKeyDown);
    return () => input.removeEventListener('keydown', handleKeyDown);
  }, [isSelected, onChange]);

  return (
    <Toggle
      {...toggleProps}
      ref={inputRef}
      isSelected={isSelected}
      onChange={onChange}
      className={clsx(css.toggleFocusRing, className)}
    />
  );
};
